import clsx from 'clsx'
import { DateTime } from 'luxon'
import { useEffect, useRef } from 'react'

import { useTranslation } from '@/shared/hooks/useTranslation'
import tabStyles from '@/shared/ui/Layout/page/PageTabs.module.scss'

import { sortDayKeysChronologically } from '../model/grouping'
import type { ScheduleDayMap } from '../model/types'

export type ScheduleDayTabsProps = {
  days: ScheduleDayMap
  selectedDay: string | null
  onSelect: (day: string) => void
}

/** Folga à esquerda do dia ativo — evita colar no canto após o auto-scroll. */
const TAB_SCROLL_LEFT_INSET_PX = 12

const scrollTabIntoView = (nav: HTMLDivElement, tab: HTMLButtonElement) => {
  const navStyles = getComputedStyle(nav)
  const paddingLeft = Number.parseFloat(navStyles.paddingLeft) || 0
  nav.scrollTo({
    left: Math.max(0, tab.offsetLeft - paddingLeft - TAB_SCROLL_LEFT_INSET_PX),
    behavior: 'smooth'
  })
}

/**
 * Seletor de dia no estilo `PageTabs` (trilho branco + item ativo roxo).
 * Filtra o conteúdo da mesma tela — visual de abas, semântica de toggle
 * (`role="group"` + `aria-pressed`), para contrastar com os `FilterChips`
 * de momento logo abaixo.
 */
export const ScheduleDayTabs = ({
  days,
  selectedDay,
  onSelect
}: ScheduleDayTabsProps) => {
  const { t } = useTranslation()
  const navRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const entries = sortDayKeysChronologically(Object.keys(days))

  useEffect(() => {
    if (!selectedDay) return

    const nav = navRef.current
    const tab = tabRefs.current[selectedDay]
    if (!nav || !tab) return

    const frame = window.requestAnimationFrame(() => {
      scrollTabIntoView(nav, tab)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [selectedDay, entries.length])

  if (!entries.length || !selectedDay) return null

  const today = DateTime.now().toISODate()

  return (
    <div
      ref={navRef}
      className={tabStyles.nav}
      role="group"
      aria-label={t('schedule.day.ariaLabel')}
    >
      {entries.map(dayKey => {
        const date = DateTime.fromISO(dayKey)
        const isActive = selectedDay === dayKey
        const label =
          dayKey === today
            ? t('schedule.day.today', { date: date.toFormat("dd 'de' MMM") })
            : date.toFormat("EEE, dd 'de' MMM", { locale: 'pt-BR' })

        return (
          <button
            key={dayKey}
            ref={node => {
              tabRefs.current[dayKey] = node
            }}
            type="button"
            className={clsx(tabStyles.item, isActive && tabStyles.itemActive)}
            aria-pressed={isActive}
            onClick={() => onSelect(dayKey)}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
